import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, CheckCircle2, Clock, ArrowLeft, IdCard } from "lucide-react";

export default function Scoreboard() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/employees/${id}/scoreboard`).then(r => setData(r.data)); }, [id]);

  if (!data) return <div className="p-10 text-slate-500">Loading…</div>;
  const completedCount = data.items.filter(x => x.completed).length;
  const totalAssigned = data.items.length;

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/employees" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4 mr-1"/>Back to Employees
        </Link>
        <Link to={`/employees/${id}/card`}>
          <Button variant="outline" className="rounded-xl" data-testid="view-training-card">
            <IdCard className="h-4 w-4 mr-1.5"/>View Training Card
          </Button>
        </Link>
      </div>

      <Card className="border-slate-200 rounded-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-sky-500 to-sky-600 p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-sky-100/80 font-bold">Employee Scoreboard</div>
              <h1 className="font-display font-black text-3xl lg:text-4xl mt-1">{data.employee.name}</h1>
              <div className="text-sky-100 mt-1">{data.employee.emp_id} · {data.employee.email}</div>
            </div>
            <div className="flex gap-3">
              <Stat label="Assigned" value={totalAssigned}/>
              <Stat label="Completed" value={completedCount}/>
              <Stat label="Attempts" value={data.total_attempts}/>
            </div>
          </div>
        </div>
      </Card>

      <h2 className="font-display font-bold text-xl">Assigned Trainings</h2>

      <div className="grid md:grid-cols-2 gap-4">
        {data.items.length === 0 && (
          <Card className="md:col-span-2 border-slate-200 rounded-2xl">
            <CardContent className="p-8 text-center text-slate-500">No trainings assigned yet.</CardContent>
          </Card>
        )}
        {data.items.map((it, idx) => {
          const pct = it.best_score != null && it.max_score ? Math.round((it.best_score/it.max_score)*100) : null;
          return (
            <Card key={idx} className="border-slate-200 rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-sky-600 font-bold">{it.questionnaire.topic || "Training"}</div>
                    <h3 className="font-display font-bold text-lg mt-1">{it.questionnaire.title}</h3>
                  </div>
                  {it.completed ? (
                    <Badge className="bg-green-100 text-green-700 border-0"><CheckCircle2 className="h-3 w-3 mr-1"/>Completed</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1"/>Pending</Badge>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                  <div><div className="text-xs text-slate-400">Attempts</div><div className="font-bold">{it.attempts}</div></div>
                  <div><div className="text-xs text-slate-400">Best Score</div><div className="font-bold">{it.best_score ?? "—"}{it.best_score != null && `/${it.max_score}`}</div></div>
                  <div><div className="text-xs text-slate-400">Performance</div>
                    {pct == null ? <div className="text-slate-400">—</div> :
                      <div className={`font-bold ${pct>=85?"text-green-600":pct>=70?"text-sky-600":pct>=50?"text-amber-600":"text-red-600"}`}>{pct}% <Trophy className="h-3 w-3 inline ml-0.5"/></div>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/10 backdrop-blur rounded-2xl px-5 py-3 text-center min-w-[80px]">
      <div className="text-3xl font-display font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-sky-100/80 font-bold mt-1">{label}</div>
    </div>
  );
}
